import BASE_URL from '../../utils/storage.js';
import { normalizeAvizForAPI } from '../../utils/avizPayload.js';

/**
 * Aviz API Service Layer
 * Handles all CRUD operations for Transport Aviz (delivery notes)
 */
export const avizAPI = {
  /**
   * Get all avize for a transport
   */
  async getAll(transportId, authToken) {
    const response = await fetch(`${BASE_URL}transport-aviz/${transportId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Token ${authToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch avize: ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Create new aviz for a transport
   */
  async create(transportId, avizData, authToken) {
    console.log('Creating Aviz with data:', avizData);
    console.log('For transport ID:', transportId);
    const payload = normalizeAvizForAPI(avizData);

    const response = await fetch(`${BASE_URL}transport-aviz/${transportId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Token ${authToken}`,
        'Content-Type': 'application/json; charset=UTF-8',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Aviz creation failed:', response.status, errorText);
      throw new Error(`Failed to create aviz: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('Aviz created successfully:', result);
    return result;
  },

  /**
   * Get single aviz details
   */
  async getById(transportId, avizId, authToken) {
    const response = await fetch(`${BASE_URL}transport-aviz/${transportId}/${avizId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Token ${authToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch aviz: ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Update aviz (partial update)
   */
  async update(transportId, avizId, updateData, authToken) {
    const payload = normalizeAvizForAPI(updateData, { mode: 'update' });
    const response = await fetch(`${BASE_URL}transport-aviz/${transportId}/${avizId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Token ${authToken}`,
        'Content-Type': 'application/json; charset=UTF-8',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update aviz: ${response.status} - ${errorText}`);
    }

    return await response.json();
  },

  /**
   * Delete aviz
   */
  async delete(transportId, avizId, authToken) {
    const response = await fetch(`${BASE_URL}transport-aviz/${transportId}/${avizId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Token ${authToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete aviz: ${response.status}`);
    }

    return true;
  },

  /**
   * Upload document for aviz
   * Uses multipart/form-data for file upload
   */
  async uploadDocument(transportId, avizId, file, authToken) {
    const formData = new FormData();
    formData.append('document', file);

    const response = await fetch(`${BASE_URL}transport-aviz/${transportId}/${avizId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Token ${authToken}`,
        // Don't set Content-Type - let browser set it with boundary for multipart
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to upload document: ${response.status} - ${errorText}`);
    }

    return await response.json();
  },

  /**
   * Create aviz with initial data from transport
   * This is a specialized method for the transport creation flow
   */
  async createFromTransport(transportId, transportData, authToken) {
    // Create minimal initial aviz data to ensure success
    // User will complete the rest in the modal
    const initialAvizData = {
      // Only send the absolute minimum fields
      // Backend will set defaults for others
    };

    console.log('Creating initial aviz with minimal data for transport:', transportId);
    console.log('Initial aviz data:', initialAvizData);

    return this.create(transportId, initialAvizData, authToken);
  },
};
